import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Quote } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { name: "", quote: "", result: "", service: "", order: 0 };

export default function TestimonialsTab() {
  const [items, setItems] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () =>
    axios
      .get(`${API}/testimonials`)
      .then((res) => setItems(res.data))
      .catch(() => toast.error("Could not load testimonials"));

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({ name: t.name, quote: t.quote, result: t.result || "", service: t.service || "", order: t.order });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.quote) {
      toast.error("Please add a client name and their quote.");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name,
      quote: form.quote,
      result: form.result || null,
      service: form.service || null,
      order: Number(form.order) || 0,
    };
    try {
      if (editing) {
        await axios.put(`${API}/admin/testimonials/${editing.id}`, payload, { withCredentials: true });
        toast.success("Testimonial updated.");
      } else {
        await axios.post(`${API}/admin/testimonials`, payload, { withCredentials: true });
        toast.success("Testimonial added.");
      }
      setDialogOpen(false);
      load();
    } catch {
      toast.error("Could not save testimonial.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete testimonial from "${t.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/testimonials/${t.id}`, { withCredentials: true });
      toast.success("Testimonial deleted.");
      load();
    } catch {
      toast.error("Could not delete testimonial.");
    }
  };

  if (!items) {
    return (
      <div className="flex items-center gap-2 text-zinc-500 py-16 justify-center">
        <Loader2 size={18} className="animate-spin text-[#D4AF37]" /> Loading testimonials…
      </div>
    );
  }

  return (
    <div data-testid="testimonials-admin-content">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h2 className="font-display text-white text-2xl font-bold uppercase tracking-tight">
            Client testimonials
          </h2>
          <p className="text-zinc-500 text-sm font-light mt-1">
            Shown publicly in the "Client Results" strip above the enquiry form.
          </p>
        </div>
        <button
          onClick={openCreate}
          data-testid="add-testimonial-button"
          className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#F3CE56] text-zinc-950 px-5 py-3 font-display text-sm font-bold uppercase tracking-[0.14em] transition-colors duration-300"
        >
          <Plus size={15} strokeWidth={2} /> Add Testimonial
        </button>
      </div>

      {items.length === 0 ? (
        <div data-testid="testimonials-empty-state" className="border border-dashed border-zinc-800 p-14 text-center">
          <p className="font-display text-zinc-400 text-xl font-bold uppercase tracking-tight">
            No testimonials yet
          </p>
          <p className="text-zinc-600 text-sm font-light mt-2">
            Add a client quote — the strip only appears on the site once you have at least one.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((t) => (
            <div
              key={t.id}
              data-testid={`admin-testimonial-${t.id}`}
              className="bg-zinc-900/50 border border-zinc-800 p-6 hover:border-zinc-700 transition-colors"
            >
              <Quote size={18} strokeWidth={1.5} className="text-[#D4AF37]/60 mb-3" />
              <p className="text-zinc-300 text-sm font-light leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
              <div className="mt-4 pt-4 border-t border-zinc-800 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-display text-white text-base font-bold uppercase tracking-wide">{t.name}</p>
                  {t.result && (
                    <p className="font-display text-[#D4AF37] text-xs font-bold uppercase tracking-[0.12em] mt-0.5">
                      {t.result}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEdit(t)}
                    data-testid={`edit-testimonial-${t.id}`}
                    className="inline-flex items-center gap-2 border border-zinc-700 hover:border-[#D4AF37] text-white px-3.5 py-2 font-display text-xs font-bold uppercase tracking-[0.12em] transition-colors"
                  >
                    <Pencil size={12} /> Edit
                  </button>
                  <button
                    onClick={() => remove(t)}
                    data-testid={`delete-testimonial-${t.id}`}
                    className="inline-flex items-center gap-2 border border-zinc-800 hover:border-red-500/60 hover:text-red-400 text-zinc-500 px-3.5 py-2 font-display text-xs font-bold uppercase tracking-[0.12em] transition-colors"
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-2xl">
              {editing ? "Edit Testimonial" : "New Testimonial"}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              Appears publicly in the Client Results section.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-2">
            <div className="sm:col-span-2">
              <Label className="text-[10px] font-semibold tracking-[0.2em] uppercase text-zinc-500">Client Name</Label>
              <Input
                data-testid="testimonial-form-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Sarah M."
                className="mt-2 rounded-none bg-zinc-950 border-zinc-800 focus-visible:border-[#D4AF37] focus-visible:ring-0 text-white h-11"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[10px] font-semibold tracking-[0.2em] uppercase text-zinc-500">Quote</Label>
              <Textarea
                data-testid="testimonial-form-quote"
                value={form.quote}
                onChange={(e) => setForm({ ...form, quote: e.target.value })}
                rows={4}
                placeholder="What did they say about training with you?"
                className="mt-2 rounded-none bg-zinc-950 border-zinc-800 focus-visible:border-[#D4AF37] focus-visible:ring-0 text-white"
              />
            </div>
            <div>
              <Label className="text-[10px] font-semibold tracking-[0.2em] uppercase text-zinc-500">
                Result highlight (optional)
              </Label>
              <Input
                data-testid="testimonial-form-result"
                value={form.result}
                onChange={(e) => setForm({ ...form, result: e.target.value })}
                placeholder="e.g. Lost 12kg in 16 weeks"
                className="mt-2 rounded-none bg-zinc-950 border-zinc-800 focus-visible:border-[#D4AF37] focus-visible:ring-0 text-white h-11"
              />
            </div>
            <div>
              <Label className="text-[10px] font-semibold tracking-[0.2em] uppercase text-zinc-500">
                Service (optional)
              </Label>
              <Input
                data-testid="testimonial-form-service"
                value={form.service}
                onChange={(e) => setForm({ ...form, service: e.target.value })}
                placeholder="e.g. 1-2-1 Sessions"
                className="mt-2 rounded-none bg-zinc-950 border-zinc-800 focus-visible:border-[#D4AF37] focus-visible:ring-0 text-white h-11"
              />
            </div>
            <div>
              <Label className="text-[10px] font-semibold tracking-[0.2em] uppercase text-zinc-500">Display order</Label>
              <Input
                type="number"
                data-testid="testimonial-form-order"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: e.target.value })}
                className="mt-2 rounded-none bg-zinc-950 border-zinc-800 focus-visible:border-[#D4AF37] focus-visible:ring-0 text-white h-11"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <button
              onClick={save}
              disabled={saving}
              data-testid="testimonial-form-save"
              className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#F3CE56] disabled:opacity-60 text-zinc-950 px-7 py-3 font-display text-sm font-bold uppercase tracking-[0.14em] transition-colors duration-300"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : null}
              {editing ? "Save Changes" : "Add Testimonial"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
